import React, { useRef, useEffect } from "react";
import { setupCanvas } from "./canvasUtils";

// ============================================
// Chapter 23  -  Calorimetry
// (23.1 already has ShowerDevelopment in PhysicsInfographics)
// ============================================

/** 23.2 – Hadronic Showers */
export const HadronicShowers = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Hadronic Showers", W / 2, 24);

    // Comparison EM vs Hadronic
    const cols = [
      {
        title: "EM Shower", x: 25, color: "#3B82F6", bg: "#DBEAFE",
        items: ["e⁺e⁻ pair + bremsstrahlung", "X₀ ~ cm scale", "Compact, well-contained", "~100% detectable energy", "Scales with ln(E)"],
      },
      {
        title: "Hadronic Shower", x: 300, color: "#EF4444", bg: "#FEE2E2",
        items: ["Nuclear interactions (π, p, n)", "λ_I ~ 10–20 cm (iron)", "Broader, irregular shape", "~30% invisible (nuclear)", "Fluctuations dominate"],
      },
    ];
    cols.forEach((col) => {
      ctx.fillStyle = col.bg;
      ctx.beginPath(); ctx.roundRect(col.x, 45, 235, 150, 10); ctx.fill();
      ctx.strokeStyle = col.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(col.x, 45, 235, 150, 10); ctx.stroke();
      ctx.fillStyle = col.color; ctx.font = "bold 13px Inter,sans-serif";
      ctx.fillText(col.title, col.x + 118, 65);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      col.items.forEach((it, i) => ctx.fillText(it, col.x + 118, 85 + i * 20));
    });

    // Hadronic shower diagram
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Hadronic Shower Development", W / 2, 215);
    // Incident hadron
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.moveTo(50, 270); ctx.lineTo(130, 270); ctx.stroke();
    ctx.fillStyle = "#EF4444";
    ctx.beginPath(); ctx.moveTo(130, 270); ctx.lineTo(123, 265); ctx.lineTo(123, 275); ctx.closePath(); ctx.fill();
    ctx.font = "11px Inter,sans-serif"; ctx.textAlign = "left"; ctx.fillText("π/p/K", 50, 260);
    ctx.textAlign = "center";
    // Nuclear interaction point
    ctx.fillStyle = "#EF4444";
    ctx.beginPath(); ctx.arc(140, 270, 5, 0, Math.PI * 2); ctx.fill();
    // Secondary particles
    const secondaries = [
      { dx: 80, dy: -40, label: "π⁺", color: "#3B82F6" },
      { dx: 100, dy: -15, label: "π⁰→γγ", color: "#F59E0B" },
      { dx: 90, dy: 10, label: "n", color: "#10B981" },
      { dx: 70, dy: 35, label: "π⁻", color: "#8B5CF6" },
      { dx: 60, dy: 55, label: "p", color: "#EF4444" },
    ];
    secondaries.forEach((s) => {
      ctx.strokeStyle = s.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(140, 270); ctx.lineTo(140 + s.dx, 270 + s.dy); ctx.stroke();
      ctx.fillStyle = s.color; ctx.font = "10px Inter,sans-serif";
      ctx.fillText(s.label, 145 + s.dx, 270 + s.dy);
    });
    // Further cascading
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(240, 255); ctx.lineTo(310, 240); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(240, 255); ctx.lineTo(310, 260); ctx.stroke();
    ctx.fillStyle = "#F59E0B"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("EM sub-showers", 360, 245);

    // Invisible energy note
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(300, 280, 230, 35, 8); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.roundRect(300, 280, 230, 35, 8); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("~30% invisible: nuclear binding,", 415, 295);
    ctx.fillText("neutrinos, slow neutrons", 415, 308);

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("λ_I ≫ X₀ → hadronic calorimeters are much deeper than EM ones", W / 2, 345);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 23.3 – Radiation Length X₀ and Interaction Length λ */
export const RadiationInteractionLength = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 380;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Radiation Length X₀ vs Interaction Length λ_I", W / 2, 24);

    // Two definition boxes
    const defs = [
      {
        title: "Radiation Length X₀", color: "#3B82F6", bg: "#DBEAFE",
        desc: "Mean distance for e⁻ to lose\n1/e of energy via bremsstrahlung\nAlso: 7/9 of pair-production mean free path",
        formula: "X₀ ≈ 716 A / (Z(Z+1) ln(287/√Z)) g/cm²",
      },
      {
        title: "Interaction Length λ_I", color: "#EF4444", bg: "#FEE2E2",
        desc: "Mean free path for nuclear\ninelastic interaction of hadrons\nDetermines hadronic shower depth",
        formula: "λ_I ≈ 35 A^(1/3) g/cm²",
      },
    ];
    defs.forEach((d, i) => {
      const y = 45 + i * 100;
      ctx.fillStyle = d.bg;
      ctx.beginPath(); ctx.roundRect(30, y, 500, 85, 10); ctx.fill();
      ctx.strokeStyle = d.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(30, y, 500, 85, 10); ctx.stroke();
      ctx.fillStyle = d.color; ctx.font = "bold 13px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(d.title, 45, y + 20);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      d.desc.split("\n").forEach((ln, li) => ctx.fillText(ln, 45, y + 38 + li * 14));
      ctx.fillStyle = "#1E293B"; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "right"; ctx.fillText(d.formula, 520, y + 75);
    });
    ctx.textAlign = "center";

    // Material table
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Comparison by Material", W / 2, 260);
    const mats = [
      { name: "Pb", Z: 82, X0: "0.56 cm", lI: "17.1 cm", ratio: "31�-" },
      { name: "Fe", Z: 26, X0: "1.76 cm", lI: "16.8 cm", ratio: "10�-" },
      { name: "Al", Z: 13, X0: "8.9 cm", lI: "39.4 cm", ratio: "4.4�-" },
      { name: "Water", Z: "~7.4", X0: "36.1 cm", lI: "83.6 cm", ratio: "2.3�-" },
    ];
    const headers = ["Material", "Z", "X₀", "λ_I", "λ_I / X₀"];
    ctx.font = "bold 11px Inter,sans-serif"; ctx.fillStyle = "#64748B";
    headers.forEach((h, i) => ctx.fillText(h, 80 + i * 105, 280));
    ctx.font = "12px Inter,sans-serif"; ctx.fillStyle = "#475569";
    mats.forEach((m, i) => {
      const y = 298 + i * 17;
      [m.name, String(m.Z), m.X0, m.lI, m.ratio].forEach((v, j) => ctx.fillText(v, 80 + j * 105, y));
    });

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("λ_I ≫ X₀ always → hadronic calorimeters need much more material", W / 2, 365);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 23.4 – Sampling vs Homogeneous Calorimeters */
export const SamplingVsHomogeneous = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 370;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Sampling vs Homogeneous Calorimeters", W / 2, 24);

    // Sampling calorimeter diagram
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(20, 50, 250, 140, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(20, 50, 250, 140, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Sampling", 145, 70);
    // Alternating layers
    for (let i = 0; i < 8; i++) {
      const x = 35 + i * 28;
      ctx.fillStyle = i % 2 === 0 ? "#94A3B8" : "#86EFAC";
      ctx.fillRect(x, 85, 22, 80);
    }
    ctx.fillStyle = "#475569"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("Absorber | Active | Absorber | ...", 145, 180);

    // Homogeneous calorimeter diagram
    ctx.fillStyle = "#EDE9FE";
    ctx.beginPath(); ctx.roundRect(290, 50, 250, 140, 10); ctx.fill();
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(290, 50, 250, 140, 10); ctx.stroke();
    ctx.fillStyle = "#6D28D9"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Homogeneous", 415, 70);
    // Solid block
    ctx.fillStyle = "#C4B5FD";
    ctx.fillRect(310, 85, 210, 80);
    ctx.fillStyle = "#6D28D9"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Single active material", 415, 128);
    ctx.fillText("(e.g. PbWO₄ crystal)", 415, 145);

    // Comparison table
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Comparison", W / 2, 215);
    const rows = [
      ["", "Sampling", "Homogeneous"],
      ["Resolution", "σ/E ~ 10–15%/√E", "σ/E ~ 2–5%/√E"],
      ["Cost", "Lower (cheaper absorber)", "Higher (crystals)"],
      ["Compactness", "Moderate", "More compact (EM)"],
      ["Hadronic?", "Yes (Fe/scint)", "Difficult"],
      ["Example", "ATLAS Tile, CMS HCAL", "CMS ECAL (PbWO₄), BaBar"],
    ];
    ctx.font = "bold 11px Inter,sans-serif"; ctx.fillStyle = "#64748B";
    rows[0].forEach((h, j) => ctx.fillText(h, 80 + j * 195, 235));
    ctx.font = "11px Inter,sans-serif";
    rows.slice(1).forEach((r, i) => {
      const y = 255 + i * 20;
      ctx.fillStyle = "#1E293B"; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(r[0], 30, y);
      ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
      ctx.textAlign = "center"; ctx.fillText(r[1], 275, y);
      ctx.fillStyle = "#8B5CF6";
      ctx.fillText(r[2], 470, y);
    });
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 23.5 – CMS Electromagnetic Calorimeter */
export const CMSEcal = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("CMS ECAL  -  Lead Tungstate Calorimeter", W / 2, 24);

    // Crystal arrangement sketch
    ctx.fillStyle = "#EDE9FE";
    ctx.beginPath(); ctx.roundRect(30, 50, 230, 130, 10); ctx.fill();
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 50, 230, 130, 10); ctx.stroke();
    ctx.fillStyle = "#6D28D9"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("PbWO₄ Crystal Array", 145, 70);
    // Draw crystal grid
    for (let r = 0; r < 4; r++) {
      for (let co = 0; co < 6; co++) {
        const cx = 55 + co * 35, cy = 85 + r * 22;
        ctx.fillStyle = "#C4B5FD";
        ctx.fillRect(cx, cy, 28, 16);
        ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 0.5;
        ctx.strokeRect(cx, cy, 28, 16);
      }
    }
    ctx.fillStyle = "#475569"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("75,848 crystals total", 145, 175);

    // Specs
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(285, 50, 250, 130, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(285, 50, 250, 130, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Key Specifications", 410, 70);
    const specs = [
      "Crystal: PbWO₄ (X₀ = 0.89 cm)",
      "Length: 23 cm (25.8 X₀)",
      "Light yield: ~4.5 p.e./MeV",
      "Photodetectors: APDs (barrel), VPTs (endcap)",
      "Resolution: 0.5% at 100 GeV",
    ];
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    specs.forEach((s, i) => ctx.fillText(s, 410, 90 + i * 18));

    // Higgs discovery note
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(40, 200, 480, 50, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(40, 200, 480, 50, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Crucial for H→γγ discovery (2012)", W / 2, 220);
    ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Excellent energy resolution allowed narrow γγ mass peak at 125 GeV", W / 2, 240);

    // Energy resolution formula
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Energy Resolution Formula", W / 2, 275);
    ctx.font = "14px Inter,sans-serif";
    ctx.fillText("σ(E)/E = 2.8%/√E ⊕ 0.12/E ⊕ 0.30%", W / 2, 298);
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("stochastic ⊕ noise ⊕ constant (intercalibration)", W / 2, 318);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 23.6 – Energy Resolution: Stochastic, Noise, and Constant Terms */
export const EnergyResolution = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Calorimeter Energy Resolution", W / 2, 24);

    // Main equation
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(60, 45, 440, 40, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(60, 45, 440, 40, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 14px Inter,sans-serif";
    ctx.fillText("σ(E)/E = a/√E  ⊕  b/E  ⊕  c", W / 2, 70);

    // Three term boxes
    const terms = [
      {
        label: "a/√E", name: "Stochastic", color: "#3B82F6", bg: "#DBEAFE",
        origin: "Statistical fluctuations\nin shower sampling",
        dom: "Dominates at\nmedium energies",
        typ: "a ~ 2–15%",
      },
      {
        label: "b/E", name: "Noise", color: "#F59E0B", bg: "#FEF3C7",
        origin: "Electronics noise,\npileup",
        dom: "Dominates at\nlow energies",
        typ: "b ~ 0.1–0.5 GeV",
      },
      {
        label: "c", name: "Constant", color: "#EF4444", bg: "#FEE2E2",
        origin: "Calibration, non-uniformity,\nleakage",
        dom: "Dominates at\nhigh energies",
        typ: "c ~ 0.3–1%",
      },
    ];
    terms.forEach((t, i) => {
      const x = 20 + i * 180, y = 105, w = 168, h = 130;
      ctx.fillStyle = t.bg;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
      ctx.strokeStyle = t.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();
      ctx.fillStyle = t.color; ctx.font = "bold 14px Inter,sans-serif";
      ctx.fillText(t.label, x + w / 2, y + 22);
      ctx.font = "bold 11px Inter,sans-serif";
      ctx.fillText(t.name, x + w / 2, y + 40);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      t.origin.split("\n").forEach((ln, li) => ctx.fillText(ln, x + w / 2, y + 60 + li * 14));
      t.dom.split("\n").forEach((ln, li) => ctx.fillText(ln, x + w / 2, y + 92 + li * 14));
      ctx.fillStyle = t.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.fillText(t.typ, x + w / 2, y + 122);
    });

    // Resolution vs energy plot
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("σ/E vs Energy (typical sampling calorimeter)", W / 2, 255);
    const pL = 80, pR = 500, pT = 270, pB = 345;
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(pL, pB); ctx.lineTo(pR, pB); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(pL, pB); ctx.lineTo(pL, pT); ctx.stroke();
    ctx.fillStyle = "#64748B"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("E (GeV)", (pL + pR) / 2, pB + 12);
    ctx.save(); ctx.translate(pL - 15, (pT + pB) / 2); ctx.rotate(-Math.PI / 2);
    ctx.fillText("σ/E", 0, 0); ctx.restore();
    // Curve
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 1; i <= 200; i++) {
      const E = (i / 200) * 200;
      const res = Math.sqrt((0.10 / Math.sqrt(E)) ** 2 + (0.3 / E) ** 2 + 0.005 ** 2);
      const x = pL + (i / 200) * (pR - pL);
      const y = pB - res * (pB - pT) / 0.35;
      i === 1 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 23.10 – Compensation and Advanced Calorimeter Techniques */
export const CompensationTechniques = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Compensation & Advanced Calorimetry", W / 2, 24);

    // Problem: e/h ratio
    ctx.fillStyle = "#FEE2E2";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 60, 10); ctx.fill();
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 60, 10); ctx.stroke();
    ctx.fillStyle = "#991B1B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Problem: e/h ≠ 1 in most calorimeters", W / 2, 68);
    ctx.fillStyle = "#EF4444"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("EM component fully visible, but hadronic has ~30% invisible energy (nuclear binding)", W / 2, 90);

    // Solutions
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Compensation Strategies", W / 2, 130);
    const strats = [
      {
        name: "Hardware\ncompensation", desc: "Tune absorber/active ratio\nso e/h → 1 (e.g. Pb/scint = 4:1)", color: "#3B82F6",
        example: "ZEUS calorimeter (U/scint)",
      },
      {
        name: "Software\ncompensation", desc: "Weight EM/had components\ndifferently event by event", color: "#8B5CF6",
        example: "H1 weighting, ATLAS local",
      },
      {
        name: "Dual readout", desc: "Measure Cherenkov (EM only)\n+ scintillation (total) separately", color: "#10B981",
        example: "DREAM / RD52 at CERN",
      },
      {
        name: "Particle flow", desc: "Combine tracker + calorimeter\nfor each particle individually", color: "#F59E0B",
        example: "CMS PF, ILC concepts",
      },
    ];
    strats.forEach((s, i) => {
      const y = 145 + i * 52;
      ctx.fillStyle = s.color + "12";
      ctx.beginPath(); ctx.roundRect(25, y, 510, 45, 8); ctx.fill();
      ctx.strokeStyle = s.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(25, y, 510, 45, 8); ctx.stroke();
      ctx.fillStyle = s.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left";
      s.name.split("\n").forEach((ln, li) => ctx.fillText(ln, 35, y + 15 + li * 13));
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      s.desc.split("\n").forEach((ln, li) => ctx.fillText(ln, 150, y + 15 + li * 13));
      ctx.fillStyle = "#64748B"; ctx.font = "10px Inter,sans-serif";
      ctx.fillText(s.example, 150, y + 40);
    });
    ctx.textAlign = "center";

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Goal: achieve e/h = 1 → linear response → Gaussian resolution for jets", W / 2, 355);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

// ============================================
// Chapter 24  -  Feynman Diagrams
// (24.1 already has FeynmanVertex in PhysicsInfographics)
// ============================================

/** 24.2 – Vertices and Coupling Constants */
export const VerticesCoupling = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Feynman Vertices & Coupling Constants", W / 2, 24);

    // Three force vertices
    const vertices = [
      {
        force: "QED", coupling: "α ≈ 1/137", vertex: "e⁻  -  γ  -  e⁻",
        color: "#3B82F6", bg: "#DBEAFE",
        lines: [
          { from: [30, 105], to: [85, 75], style: "solid", label: "e⁻" },
          { from: [85, 75], to: [140, 105], style: "solid", label: "e⁻" },
          { from: [85, 75], to: [85, 40], style: "wavy", label: "γ" },
        ],
      },
      {
        force: "QCD", coupling: "α_s ≈ 0.12", vertex: "q  -  g  -  q",
        color: "#10B981", bg: "#D1FAE5",
        lines: [
          { from: [210, 105], to: [265, 75], style: "solid", label: "q" },
          { from: [265, 75], to: [320, 105], style: "solid", label: "q" },
          { from: [265, 75], to: [265, 40], style: "curly", label: "g" },
        ],
      },
      {
        force: "Weak", coupling: "G_F ~ 10⁻⁵", vertex: "e  -  W  -  ν",
        color: "#EF4444", bg: "#FEE2E2",
        lines: [
          { from: [390, 105], to: [445, 75], style: "solid", label: "e⁻" },
          { from: [445, 75], to: [500, 105], style: "solid", label: "ν_e" },
          { from: [445, 75], to: [445, 40], style: "wavy", label: "W" },
        ],
      },
    ];
    vertices.forEach((v) => {
      // Draw vertex lines
      v.lines.forEach((l) => {
        ctx.strokeStyle = v.color; ctx.lineWidth = 2;
        if (l.style === "wavy") {
          ctx.beginPath();
          const dx = l.to[0] - l.from[0], dy = l.to[1] - l.from[1];
          const len = Math.sqrt(dx * dx + dy * dy);
          const steps = 8;
          for (let s = 0; s <= steps; s++) {
            const t = s / steps;
            const x = l.from[0] + dx * t + (s % 2 === 0 ? 5 : -5) * (dy / len);
            const y = l.from[1] + dy * t - (s % 2 === 0 ? 5 : -5) * (dx / len);
            s === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          }
          ctx.stroke();
        } else if (l.style === "curly") {
          ctx.beginPath();
          const dx = l.to[0] - l.from[0], dy = l.to[1] - l.from[1];
          const len = Math.sqrt(dx * dx + dy * dy);
          const steps = 12;
          for (let s = 0; s <= steps; s++) {
            const t = s / steps;
            const amp = 6 * Math.sin(t * Math.PI * 4);
            const x = l.from[0] + dx * t + amp * (dy / len);
            const y = l.from[1] + dy * t - amp * (dx / len);
            s === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          }
          ctx.stroke();
        } else {
          ctx.beginPath(); ctx.moveTo(...l.from); ctx.lineTo(...l.to); ctx.stroke();
        }
        ctx.fillStyle = v.color; ctx.font = "10px Inter,sans-serif";
        ctx.fillText(l.label, (l.from[0] + l.to[0]) / 2 + 8, (l.from[1] + l.to[1]) / 2);
      });
      // Vertex dot
      ctx.fillStyle = v.color;
      ctx.beginPath(); ctx.arc(v.lines[0].to[0], v.lines[0].to[1], 4, 0, Math.PI * 2); ctx.fill();
    });

    // Force labels
    vertices.forEach((v, i) => {
      const cx = 85 + i * 180;
      ctx.fillStyle = v.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.fillText(v.force, cx, 125);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(v.coupling, cx, 140);
    });

    // Coupling constant explanation
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Coupling Constant Rules", W / 2, 170);
    const rules = [
      { rule: "Each vertex contributes √α to amplitude", color: "#3B82F6" },
      { rule: "Probability ∝ |amplitude|² → each vertex costs factor α", color: "#8B5CF6" },
      { rule: "n vertices → cross section ∝ α^n  (higher order = suppressed)", color: "#EF4444" },
    ];
    rules.forEach((r, i) => {
      ctx.fillStyle = r.color; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(r.rule, W / 2, 190 + i * 18);
    });

    // Running coupling
    ctx.fillStyle = "#F8FAFC";
    ctx.beginPath(); ctx.roundRect(30, 240, 500, 105, 10); ctx.fill();
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.roundRect(30, 240, 500, 105, 10); ctx.stroke();
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Running of Coupling Constants", W / 2, 260);
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("α_QED: increases at higher Q² (screening)", W / 2, 280);
    ctx.fillText("α_QCD: decreases at higher Q² → asymptotic freedom", W / 2, 300);
    ctx.fillText("α₁ (hypercharge): increases with Q²  -  all three converge at GUT scale", W / 2, 320);
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("All three may unify at E ~ 10¹⁶ GeV (GUT scale)", W / 2, 340);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 24.3 – Propagators: Internal Lines and Virtual Particles */
export const Propagators = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Propagators  -  Virtual Particles", W / 2, 24);

    // Propagator types
    const props = [
      { name: "Fermion", style: "solid + arrow", formula: "i(γ·p + m)/(p²−m²)", color: "#3B82F6" },
      { name: "Photon (γ)", style: "wavy line", formula: "−ig_μν /q²", color: "#F59E0B" },
      { name: "Gluon (g)", style: "curly (spring)", formula: "−iδ^ab g_μν /q²", color: "#10B981" },
      { name: "W/Z boson", style: "wavy + mass", formula: "i(−g_μν+q_μq_ν/M²)/(q²−M²)", color: "#EF4444" },
    ];
    props.forEach((p, i) => {
      const y = 50 + i * 45;
      ctx.fillStyle = p.color + "12";
      ctx.beginPath(); ctx.roundRect(20, y, 520, 38, 8); ctx.fill();
      ctx.strokeStyle = p.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(20, y, 520, 38, 8); ctx.stroke();
      // Draw line style sample
      ctx.strokeStyle = p.color; ctx.lineWidth = 2;
      if (p.name === "Fermion") {
        ctx.beginPath(); ctx.moveTo(35, y + 19); ctx.lineTo(90, y + 19); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(75, y + 14); ctx.lineTo(85, y + 19); ctx.lineTo(75, y + 24); ctx.stroke();
      } else if (p.name === "Photon (γ)" || p.name === "W/Z boson") {
        ctx.beginPath();
        for (let s = 0; s <= 10; s++) {
          const x = 35 + s * 5.5;
          const yy = y + 19 + (s % 2 === 0 ? -4 : 4);
          s === 0 ? ctx.moveTo(x, yy) : ctx.lineTo(x, yy);
        }
        ctx.stroke();
      } else {
        ctx.beginPath();
        for (let s = 0; s <= 16; s++) {
          const x = 35 + s * 3.5;
          const yy = y + 19 + 5 * Math.sin(s * Math.PI * 0.8);
          s === 0 ? ctx.moveTo(x, yy) : ctx.lineTo(x, yy);
        }
        ctx.stroke();
      }
      ctx.fillStyle = p.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(p.name, 100, y + 17);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(p.style, 100, y + 32);
      ctx.fillStyle = "#1E293B"; ctx.font = "12px Inter,sans-serif";
      ctx.textAlign = "right"; ctx.fillText(p.formula, 530, y + 24);
    });
    ctx.textAlign = "center";

    // Virtual particle explanation
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(30, 240, 500, 85, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 240, 500, 85, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Virtual Particles  -  Off Mass Shell", W / 2, 260);
    ctx.fillStyle = "#78350F"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("E² ≠ (pc)² + (mc²)²  →  'virtual' (not directly observed)", W / 2, 280);
    ctx.fillText("Exist for time Δt ~ ℏ/ΔE  (Heisenberg uncertainty)", W / 2, 298);
    ctx.fillText("Propagator ∝ 1/(q²−m²): heavier mediator → shorter range", W / 2, 316);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 24.4 – Discovery of the Gluon via 3-Jet Events */
export const GluonDiscovery = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Gluon Discovery  -  PETRA 3-Jet Events (1979)", W / 2, 24);

    // 2-jet vs 3-jet diagrams
    // 2-jet: e+e- → q q-bar
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(20, 50, 245, 150, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(20, 50, 245, 150, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("2-Jet Event (QED)", 142, 70);
    // Draw Feynman: e+e- → γ* → qq̄
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(50, 100); ctx.lineTo(100, 130); ctx.stroke(); // e-
    ctx.beginPath(); ctx.moveTo(50, 160); ctx.lineTo(100, 130); ctx.stroke(); // e+
    // Virtual photon
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath();
    for (let s = 0; s <= 10; s++) {
      const x = 100 + s * 6;
      const y = 130 + (s % 2 === 0 ? -4 : 4);
      s === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(160, 130); ctx.lineTo(230, 100); ctx.stroke(); // q
    ctx.beginPath(); ctx.moveTo(160, 130); ctx.lineTo(230, 160); ctx.stroke(); // q̄
    ctx.fillStyle = "#3B82F6"; ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("e⁻", 38, 97); ctx.fillText("e⁺", 38, 168);
    ctx.fillText("q", 232, 98); ctx.fillText("q̄", 232, 168);
    ctx.textAlign = "center";
    ctx.fillText("γ*", 130, 122);

    // 3-jet: e+e- → q q-bar g
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(290, 50, 250, 150, 10); ctx.fill();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(290, 50, 250, 150, 10); ctx.stroke();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("3-Jet Event (QCD!)", 415, 70);
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(315, 100); ctx.lineTo(365, 130); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(315, 160); ctx.lineTo(365, 130); ctx.stroke();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath();
    for (let s = 0; s <= 10; s++) {
      const x = 365 + s * 6;
      const y = 130 + (s % 2 === 0 ? -4 : 4);
      s === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(425, 130); ctx.lineTo(505, 95); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(425, 130); ctx.lineTo(505, 160); ctx.stroke();
    // Gluon radiation from quark
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath();
    for (let s = 0; s <= 10; s++) {
      const t = s / 10;
      const x = 465 + t * 40;
      const y = 127 + 5 * Math.sin(s * Math.PI * 0.8) - t * 30;
      s === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.fillStyle = "#3B82F6"; ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("e⁻", 303, 97); ctx.fillText("e⁺", 303, 168);
    ctx.fillText("q", 508, 90); ctx.fillText("q̄", 508, 168);
    ctx.fillStyle = "#10B981"; ctx.fillText("g", 512, 113);
    ctx.textAlign = "center";

    // Timeline
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(30, 215, 500, 50, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 215, 500, 50, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("TASSO, JADE, Mark J, PLUTO at PETRA (DESY, Hamburg), 1979", W / 2, 235);
    ctx.font = "12px Inter,sans-serif";
    ctx.fillText("e⁺e⁻ at √s = 27–46 GeV: clear 3-jet events → direct evidence for gluon", W / 2, 255);

    // Significance
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("3rd jet angular distribution matches spin-1 boson (gluon) prediction", W / 2, 285);
    ctx.fillText("Confirmed QCD as the theory of the strong interaction", W / 2, 305);
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Feynman diagram: additional gluon vertex gives q q̄ g final state", W / 2, 330);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 24.5 – QED Examples: Electron Scattering and Pair Annihilation */
export const QEDExamples = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("QED Processes  -  Feynman Diagrams", W / 2, 24);

    const drawWavy = (x1, y1, x2, y2, color) => {
      ctx.strokeStyle = color; ctx.lineWidth = 2;
      const dx = x2 - x1, dy = y2 - y1;
      const len = Math.sqrt(dx * dx + dy * dy);
      const steps = Math.round(len / 5);
      ctx.beginPath();
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        const px = x1 + dx * t + (s % 2 === 0 ? 4 : -4) * (dy / len);
        const py = y1 + dy * t - (s % 2 === 0 ? 4 : -4) * (dx / len);
        s === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.stroke();
    };

    // Process 1: Møller scattering (e-e- → e-e-)
    const p1x = 30, p1y = 55;
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(p1x, p1y, 240, 120, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.roundRect(p1x, p1y, 240, 120, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Møller: e⁻e⁻ → e⁻e⁻", p1x + 120, p1y + 18);
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(p1x + 20, p1y + 40); ctx.lineTo(p1x + 80, p1y + 60); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(p1x + 20, p1y + 100); ctx.lineTo(p1x + 80, p1y + 80); ctx.stroke();
    drawWavy(p1x + 80, p1y + 60, p1x + 80, p1y + 80, "#F59E0B");
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(p1x + 80, p1y + 60); ctx.lineTo(p1x + 200, p1y + 40); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(p1x + 80, p1y + 80); ctx.lineTo(p1x + 200, p1y + 100); ctx.stroke();

    // Process 2: Bhabha scattering (e+e- → e+e-)
    const p2x = 290, p2y = 55;
    ctx.fillStyle = "#EDE9FE";
    ctx.beginPath(); ctx.roundRect(p2x, p2y, 240, 120, 10); ctx.fill();
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.roundRect(p2x, p2y, 240, 120, 10); ctx.stroke();
    ctx.fillStyle = "#6D28D9"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Bhabha: e⁺e⁻ → e⁺e⁻", p2x + 120, p2y + 18);
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(p2x + 20, p2y + 40); ctx.lineTo(p2x + 80, p2y + 70); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(p2x + 20, p2y + 100); ctx.lineTo(p2x + 80, p2y + 70); ctx.stroke();
    drawWavy(p2x + 80, p2y + 70, p2x + 160, p2y + 70, "#F59E0B");
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(p2x + 160, p2y + 70); ctx.lineTo(p2x + 220, p2y + 40); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(p2x + 160, p2y + 70); ctx.lineTo(p2x + 220, p2y + 100); ctx.stroke();

    // Process 3: Pair annihilation (e+e- → γγ)
    const p3x = 30, p3y = 200;
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(p3x, p3y, 240, 120, 10); ctx.fill();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.roundRect(p3x, p3y, 240, 120, 10); ctx.stroke();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Annihilation: e⁺e⁻ → γγ", p3x + 120, p3y + 18);
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(p3x + 20, p3y + 40); ctx.lineTo(p3x + 100, p3y + 65); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(p3x + 20, p3y + 100); ctx.lineTo(p3x + 100, p3y + 65); ctx.stroke();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(p3x + 100, p3y + 65); ctx.lineTo(p3x + 130, p3y + 65); ctx.stroke();
    drawWavy(p3x + 130, p3y + 65, p3x + 210, p3y + 40, "#F59E0B");
    drawWavy(p3x + 130, p3y + 65, p3x + 210, p3y + 95, "#F59E0B");

    // Process 4: Compton scattering
    const p4x = 290, p4y = 200;
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(p4x, p4y, 240, 120, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.roundRect(p4x, p4y, 240, 120, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Compton: eγ → eγ", p4x + 120, p4y + 18);
    drawWavy(p4x + 20, p4y + 40, p4x + 80, p4y + 65, "#F59E0B");
    ctx.strokeStyle = "#92400E"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(p4x + 20, p4y + 100); ctx.lineTo(p4x + 80, p4y + 65); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(p4x + 80, p4y + 65); ctx.lineTo(p4x + 160, p4y + 65); ctx.stroke();
    drawWavy(p4x + 160, p4y + 65, p4x + 220, p4y + 40, "#F59E0B");
    ctx.strokeStyle = "#92400E"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(p4x + 160, p4y + 65); ctx.lineTo(p4x + 220, p4y + 100); ctx.stroke();

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("All tree-level QED processes: 2 vertices → σ ∝ α² ~ (1/137)² ≈ 5�-10⁻⁵", W / 2, 345);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 24.6 – Higher-Order Corrections and Loop Diagrams */
export const LoopDiagrams = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Higher-Order Corrections & Loop Diagrams", W / 2, 24);

    // Perturbation series
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 45, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 45, 500, 45, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("M = M₀ + α·M₁ + α²·M₂ + ...  (perturbation expansion)", W / 2, 72);

    // Tree level vs 1-loop vs 2-loop
    const levels = [
      { name: "Tree level", order: "α", loops: 0, accuracy: "Leading order", color: "#3B82F6" },
      { name: "1-loop (NLO)", order: "α²", loops: 1, accuracy: "~0.1% correction", color: "#8B5CF6" },
      { name: "2-loop (NNLO)", order: "α³", loops: 2, accuracy: "~0.001% correction", color: "#EF4444" },
    ];
    levels.forEach((l, i) => {
      const x = 25 + i * 180, y = 110, w = 168, h = 100;
      ctx.fillStyle = l.color + "12";
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
      ctx.strokeStyle = l.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();
      ctx.fillStyle = l.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.fillText(l.name, x + w / 2, y + 20);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(`Order: ${l.order}`, x + w / 2, y + 40);
      ctx.fillText(`Loops: ${l.loops}`, x + w / 2, y + 57);
      ctx.fillText(l.accuracy, x + w / 2, y + 74);
      // Draw loop symbol
      if (l.loops > 0) {
        ctx.strokeStyle = l.color; ctx.lineWidth = 1.5;
        for (let lo = 0; lo < l.loops; lo++) {
          ctx.beginPath();
          ctx.arc(x + w / 2 - 10 + lo * 20, y + 90, 6, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
    });

    // Famous loop predictions
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Triumphs of Loop Calculations", W / 2, 235);
    const triumphs = [
      { name: "Anomalous magnetic moment (g−2)", result: "Theory matches experiment to 12 digits!", color: "#3B82F6" },
      { name: "Lamb shift (hydrogen)", result: "QED loops shift 2S above 2P by 1058 MHz", color: "#8B5CF6" },
      { name: "Running of α_s", result: "Predicts asymptotic freedom confirmed at LEP/LHC", color: "#10B981" },
    ];
    triumphs.forEach((t, i) => {
      ctx.fillStyle = t.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(t.name, 40, 255 + i * 22);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(t.result, 280, 255 + i * 22);
    });
    ctx.textAlign = "center";

    // Divergences note
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(40, 310, 480, 40, 8); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.roundRect(40, 310, 480, 40, 8); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Loop integrals diverge! Fixed by renormalization  -  absorb infinities", W / 2, 325);
    ctx.fillText("into physical mass and charge definitions", W / 2, 342);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 24.10 – Feynman Rules and Reading Complex Diagrams */
export const FeynmanRules = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 380;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Feynman Rules  -  QED Recipe", W / 2, 24);

    // Rules list
    const rules = [
      { step: "1", rule: "Draw all topologically distinct diagrams", detail: "for given initial → final state" },
      { step: "2", rule: "Assign 4-momentum to every line", detail: "enforce conservation at each vertex" },
      { step: "3", rule: "External lines → wave functions", detail: "u(p), v(p) for fermions; ε_μ for photons" },
      { step: "4", rule: "Internal lines → propagators", detail: "i(γ·p+m)/(p²−m²) or −ig_μν/q²" },
      { step: "5", rule: "Each vertex → coupling factor", detail: "−ieγ_μ  (QED vertex factor)" },
      { step: "6", rule: "Integrate over loop momenta", detail: "∫d⁴k/(2π)⁴ for each undetermined loop" },
      { step: "7", rule: "Apply fermion signs", detail: "Relative minus for closed fermion loops" },
      { step: "8", rule: "Calculate |M|² → cross section", detail: "σ = |M|² �- phase space / flux" },
    ];
    rules.forEach((r, i) => {
      const y = 42 + i * 38;
      const color = ["#3B82F6", "#8B5CF6", "#10B981", "#EF4444", "#F59E0B", "#3B82F6", "#8B5CF6", "#10B981"][i];
      ctx.fillStyle = color; ctx.font = "bold 14px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(r.step, 25, y + 14);
      ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
      ctx.fillText(r.rule, 50, y + 14);
      ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(r.detail, 50, y + 30);
    });
    ctx.textAlign = "center";

    // Key insight
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(30, 348, 500, 25, 8); ctx.fill();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.roundRect(30, 348, 500, 25, 8); ctx.stroke();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Each line & vertex → a specific mathematical factor → multiply all → M", W / 2, 365);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};
